/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { Component, Input, Output, EventEmitter, OnInit, APP_INITIALIZER, ViewChild } from "@angular/core";
import { ParameterValue } from "../simple-function-action-editor.component";
import { DriverObjectInstance } from "app/models/driver-object-instance";
import { DriverObjectService } from "app/services/driver-object.service";
import { Event } from "app/models/event";
import { PropertyType } from "app/models/property-type";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from "app/components/simple-success-error-dialog-box/simple-success-error-dialog-box.component";

@Component({
  selector: 'app-function-parameter-editor-driver-object',
  templateUrl: './driver-object-function-parameter-editor.component.html'
})
export class DriverObjectFunctionParameterEditorComponent implements OnInit {

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  @Input()
  set event(event: Event) {
    this.mEvent = event;
    this.refreshSelectOptions(false, true);
  }

  //Event Trigger for the Rule / Reaction
  mEvent: Event;

  @Input()
  parameterValue: ParameterValue;


  @Output()
  valueChange = new EventEmitter<void>();


  //Boolean used to indicate if the Select Options are ready to be displayed or not
  mRefreshingSelectOptions = true;

  //Available Values for the Parameter Value
  mDriverObjectOptions: SelectOption[] = [];
  mEventOptions: SelectOption[] = [];
  mSelectOptions: SelectOption[] = [];


  get selectedOption(): SelectOption {
    if (this.parameterValue != null && this.parameterValue.mValue != null && this.parameterValue.mValue.length > 0) {
      return this.mSelectOptions.find(option => option.mValue == this.parameterValue.mValue);
    }
    return null;
  }
  set selectedOption(option: SelectOption) {
    if (this.parameterValue != null) {
      this.parameterValue.mValue = option == null? null : option.mValue;
      this.valueChange.emit();
    }
  }



  constructor(
    private mDriverObjectService: DriverObjectService
  ) { }

  ngOnInit(): void {
    this.refreshSelectOptions(true, true);
  }



  async refreshSelectOptions(refreshObjectOptions: boolean, refreshEventOptions: boolean): Promise<void> {
    this.mRefreshingSelectOptions = true;

    //If needs be, refresh the Object Options
    if (refreshObjectOptions) {
      await this.refreshDriverObjectOptions();
    }
    if (refreshEventOptions) {
      await this.refreshEventOptions();
    }

    //Refresh the Select Options
    const options: SelectOption[] = [];
    options.push(...this.mEventOptions);
    options.push(...this.mDriverObjectOptions);

    if (this.parameterValue != null && this.parameterValue.mValue != null && this.parameterValue.mValue.length > 0) {
      if (options.find(option => option.mValue == this.parameterValue.mValue) == null) {
        const unknownValueOption = new SelectOption();
        unknownValueOption.mValue = this.parameterValue.mValue;
        unknownValueOption.mDisplay = "UNKNOWN VALUE => " + this.parameterValue.mValue;
        options.push(unknownValueOption);
      }
    }
    
    this.mSelectOptions = options;

    this.mRefreshingSelectOptions = false;
  }

  private async refreshDriverObjectOptions(): Promise<void> {
    let driverObjects: DriverObjectInstance[];
    try {
      driverObjects = await this.mDriverObjectService.getAllDriverObjectInstances();
    } catch (error) {
      this.handleError(error);
    }

    if (driverObjects == null) {
      this.mDriverObjectOptions = [];
    } else {
      const options: SelectOption[] = [];
      for (const driverObject of driverObjects) {
        const option = new SelectOption();
        option.mValue = driverObject.id.toString();
        option.mDisplay = driverObject.driverObject.name + " - " + driverObject.name;
        options.push(option);
      }
      this.mDriverObjectOptions = options;
    }
  }

  private async refreshEventOptions(): Promise<void> {
    if (this.mEvent == null || this.mEvent.properties == null) {
      this.mEventOptions = [];
    } else {
      const options: SelectOption[] = [];
      for (const eventProperty of this.mEvent.properties) {
        if (eventProperty.type == PropertyType.DRIVEROBJECT_REFERENCE) {
          const option = new SelectOption();
          option.mValue = "${event." + eventProperty.property + "}";
          option.mDisplay = "FROM EVENT => " + eventProperty.name;
          options.push(option);
        }
      }
      this.mEventOptions = options;
    }
  }

  handleError(error: any): void {
    console.error("handleError() - Error: " + error);
    if (error != null) {
      const response: Response = error;
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, response.text().toString());
    }
  }

}

class SelectOption {
  mValue: string;
  mDisplay: string;
}
